import { BookingItem } from '@rsmaids/shared/types/bookings';
import { intlLocale } from '../i18n/locale';
import i18n from '../i18n';

// Real bookings-analytics engine — shared by the desktop Booking Analytics
// page and the mobile Bookings → View Analytics modal. Given a flat list of
// bookings it derives KPI totals, a time-bucketed series, top services/clients
// and previous-period comparisons for a chosen range.

export type AnalyticsRange = '1d' | '7d' | '14d' | '30d' | '90d' | '6m' | '12m' | 'custom';

export interface BucketStat {
  label: string;        // short x-axis label
  tooltipLabel: string; // full range shown on hover
  total: number;
  confirmed: number;
  cancelled: number;
  pending: number;
  revenue: number;
}

export interface ServiceStat { name: string; count: number; pct: number; }
export interface ClientStat { name: string; count: number; revenue: number; pct: number; }

export interface BookingsAnalytics {
  total: number;
  confirmed: number;
  cancelled: number;
  pending: number;
  completionRate: number;
  cancellationRate: number;
  revenue: number;
  avgValue: number;
  uniqueClients: number;
  buckets: BucketStat[];
  topServices: ServiceStat[];
  topClients: ClientStat[];
  prevTotal: number;
  prevRevenue: number;
}

export const EMPTY_ANALYTICS: BookingsAnalytics = {
  total: 0, confirmed: 0, cancelled: 0, pending: 0,
  completionRate: 0, cancellationRate: 0, revenue: 0, avgValue: 0,
  uniqueClients: 0, buckets: [], topServices: [], topClients: [], prevTotal: 0, prevRevenue: 0,
};

export const ANALYTICS_RANGES: { key: AnalyticsRange; label: string }[] = [
  { key: '1d', get label() { return i18n.t('utils.bookingsAnalytics.today'); } },
  { key: '7d', get label() { return i18n.t('utils.bookingsAnalytics.7Days'); } },
  { key: '14d', get label() { return i18n.t('utils.bookingsAnalytics.14Days'); } },
  { key: '30d', get label() { return i18n.t('utils.bookingsAnalytics.30Days'); } },
  { key: '90d', get label() { return i18n.t('utils.bookingsAnalytics.90Days'); } },
  { key: '6m', get label() { return i18n.t('utils.bookingsAnalytics.6Months'); } },
  { key: '12m', get label() { return i18n.t('utils.bookingsAnalytics.12Months'); } },
  { key: 'custom', get label() { return i18n.t('utils.bookingsAnalytics.custom'); } },
];

const statusOf = (b: BookingItem) => (b.status || '').toLowerCase();
export const isConfirmed = (b: BookingItem) => {
  const s = statusOf(b);
  return s === 'confirmed' || s === 'completed';
};
export const isCancelled = (b: BookingItem) => {
  const s = statusOf(b);
  return s === 'cancelled' || s === 'canceled';
};
export const isPending = (b: BookingItem) => statusOf(b) === 'pending';
// A job is done when Wix attendance is ATTENDED (flagged onto isCompleted by withAttendance) or
// it was invoiced/paid in Zoho (older jobs finished before attendance existed).
export const isDone = (b: BookingItem) =>
  !isCancelled(b) && (b.isCompleted === true || b.zohoPaid === true || !!b.zohoPaymentStatus);

// Flags bookings that Wix reports as ATTENDED (isCompleted = true). On any failure the bookings
// come back unflagged so the analytics still render.
export async function withAttendance(
  bookings: BookingItem[],
  getAttendance: (ids: string[]) => Promise<Record<string, string>>,
): Promise<BookingItem[]> {
  const ids = bookings.map(b => b.id).filter(Boolean);
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += 100) chunks.push(ids.slice(i, i + 100));
  try {
    const parts = await Promise.all(chunks.map(c => getAttendance(c)));
    const statuses = Object.assign({}, ...parts) as Record<string, string>;
    return bookings.map(b => (statuses[b.id] === 'ATTENDED' ? { ...b, isCompleted: true } : b));
  } catch (err) {
    console.error('[bookingsAnalytics] Failed to load attendance:', err);
    return bookings;
  }
}
const bDate = (b: BookingItem) =>
  b.startTime instanceof Date ? b.startTime : new Date(b.startTime as string);
const bPrice = (b: BookingItem) =>
  Number((b as BookingItem & { price?: number }).price) || 0;

function buildBuckets(from: Date, to: Date, range: AnalyticsRange) {
  type B = { start: Date; end: Date; label: string; tooltipLabel: string };
  const md = (d: Date) => d.toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric' });
  const out: B[] = [];
  if (range === '1d') {
    const midnight = new Date(from); midnight.setHours(0, 0, 0, 0);
    const fmtH = (x: number) => x === 0 ? '12 AM' : x < 12 ? `${x} AM` : x === 12 ? '12 PM' : `${x - 12} PM`;
    for (let i = 0; i < 8; i++) {
      const start = new Date(midnight.getTime() + i * 3 * 3_600_000);
      const end = new Date(start.getTime() + 3 * 3_600_000);
      out.push({ start, end, label: fmtH(start.getHours()), tooltipLabel: `${fmtH(start.getHours())} – ${fmtH(end.getHours())}` });
    }
  } else if (range === '7d' || range === '14d') {
    const n = range === '7d' ? 7 : 14;
    for (let i = 0; i < n; i++) {
      const start = new Date(from); start.setDate(from.getDate() + i); start.setHours(0, 0, 0, 0);
      const end = new Date(start); end.setDate(start.getDate() + 1);
      const label = start.toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric' });
      out.push({ start, end, label, tooltipLabel: label });
    }
  } else if (range === '30d') {
    for (let i = 0; i < 10; i++) {
      const start = new Date(from); start.setDate(from.getDate() + i * 3); start.setHours(0, 0, 0, 0);
      const end = new Date(start); end.setDate(start.getDate() + 3);
      const endDisplay = new Date(end); endDisplay.setDate(end.getDate() - 1);
      out.push({ start, end, label: md(start), tooltipLabel: `${md(start)} – ${md(endDisplay)}` });
    }
  } else if (range === '90d') {
    for (let i = 0; i < 9; i++) {
      const start = new Date(from); start.setDate(from.getDate() + i * 10); start.setHours(0, 0, 0, 0);
      const end = new Date(start); end.setDate(start.getDate() + 10);
      const endDisplay = new Date(end); endDisplay.setDate(end.getDate() - 1);
      out.push({ start, end, label: md(start), tooltipLabel: `${md(start)} – ${md(endDisplay)}` });
    }
  } else if (range === '6m' || range === '12m') {
    const n = range === '6m' ? 6 : 12;
    const base = new Date(from.getFullYear(), from.getMonth(), 1);
    for (let i = 0; i < n; i++) {
      const start = new Date(base.getFullYear(), base.getMonth() + i, 1);
      const end = new Date(base.getFullYear(), base.getMonth() + i + 1, 1);
      const label = start.toLocaleDateString(intlLocale(), { month: 'short', year: '2-digit' });
      out.push({ start, end, label, tooltipLabel: label });
    }
  } else {
    const bucketMs = (to.getTime() - from.getTime()) / 8;
    for (let i = 0; i < 8; i++) {
      const start = new Date(from.getTime() + i * bucketMs);
      const end = new Date(from.getTime() + (i + 1) * bucketMs);
      const endDisplay = new Date(end.getTime() - 86_400_000);
      out.push({ start, end, label: md(start), tooltipLabel: `${md(start)} – ${md(endDisplay)}` });
    }
  }
  return out;
}

export function rangeBounds(
  range: AnalyticsRange,
  customFrom?: Date,
  customTo?: Date,
): { from: Date; to: Date } {
  const to = (range === 'custom' && customTo) ? customTo : new Date();
  const from = (range === 'custom' && customFrom) ? customFrom : (() => {
    const d = new Date(to);
    if (range === '1d') d.setDate(to.getDate() - 1);
    else if (range === '7d') d.setDate(to.getDate() - 7);
    else if (range === '14d') d.setDate(to.getDate() - 14);
    else if (range === '30d') d.setDate(to.getDate() - 30);
    else if (range === '90d') d.setDate(to.getDate() - 90);
    else if (range === '6m') d.setMonth(to.getMonth() - 6);
    else d.setFullYear(to.getFullYear() - 1);
    return d;
  })();
  return { from, to };
}

export function computeBookingsAnalytics(
  bookings: BookingItem[],
  range: AnalyticsRange,
  customFrom?: Date,
  customTo?: Date,
): BookingsAnalytics {
  const { from, to } = rangeBounds(range, customFrom, customTo);
  const duration = to.getTime() - from.getTime();
  const prevFrom = new Date(from.getTime() - duration);

  const cur = bookings.filter(b => { const d = bDate(b); return d >= from && d <= to; });
  const prev = bookings.filter(b => { const d = bDate(b); return d >= prevFrom && d < from; });

  const total = cur.length;
  const confirmed = cur.filter(isConfirmed).length;
  const cancelled = cur.filter(isCancelled).length;
  const pending = cur.filter(isPending).length;
  const revenue = cur.reduce((s, b) => s + bPrice(b), 0);
  const avgValue = confirmed > 0 ? revenue / confirmed : 0;
  const uniqueClients = new Set(
    cur.map(b => (b.contact as unknown as { contactId?: string })?.contactId || b.contact?.name || 'unknown'),
  ).size;
  const completionRate = total > 0 ? Math.round((cur.filter(isDone).length / total) * 100) : 0;
  const cancellationRate = total > 0 ? Math.round((cancelled / total) * 100) : 0;

  const buckets: BucketStat[] = buildBuckets(from, to, range).map(({ start, end, label, tooltipLabel }) => {
    const slice = cur.filter(b => { const d = bDate(b); return d >= start && d < end; });
    return {
      label,
      tooltipLabel,
      total: slice.length,
      confirmed: slice.filter(isConfirmed).length,
      cancelled: slice.filter(isCancelled).length,
      pending: slice.filter(isPending).length,
      revenue: slice.reduce((s, b) => s + bPrice(b), 0),
    };
  });

  const svcMap: Record<string, number> = {};
  for (const b of cur) {
    const nm = String(b.serviceType || 'Other');
    svcMap[nm] = (svcMap[nm] || 0) + 1;
  }
  const topServices: ServiceStat[] = Object.entries(svcMap)
    .sort((a, b) => b[1] - a[1]).slice(0, 5)
    .map(([name, count]) => ({ name, count, pct: total > 0 ? Math.round((count / total) * 100) : 0 }));

  const cliMap: Record<string, { count: number; revenue: number }> = {};
  for (const b of cur) {
    const key = b.contact?.name || (b.contact as unknown as { contactId?: string })?.contactId || 'Unknown';
    if (!cliMap[key]) cliMap[key] = { count: 0, revenue: 0 };
    cliMap[key].count += 1;
    cliMap[key].revenue += bPrice(b);
  }
  const topClients: ClientStat[] = Object.entries(cliMap)
    .sort((a, b) => b[1].count - a[1].count).slice(0, 8)
    .map(([name, v]) => ({ name, count: v.count, revenue: v.revenue, pct: total > 0 ? Math.round((v.count / total) * 100) : 0 }));

  return {
    total, confirmed, cancelled, pending, completionRate, cancellationRate,
    revenue, avgValue, uniqueClients, buckets, topServices, topClients,
    prevTotal: prev.length,
    prevRevenue: prev.reduce((s, b) => s + bPrice(b), 0),
  };
}

export function trendPct(curr: number, prev: number): { pct: number; up: boolean } | null {
  if (prev === 0) return null;
  const p = Math.round(((curr - prev) / prev) * 100);
  return { pct: Math.abs(p), up: p >= 0 };
}

export const fmtMoney = (n: number) =>
  '$' + n.toLocaleString(undefined, { maximumFractionDigits: 0 });
